// components/company/forms/CompanyProfileForm.jsx
import { useState, useMemo, useEffect } from "react";
import { Field } from "../../ui/Field";
import { ToggleYN } from "../../ui/FormControls";
import { SERVICE_OPTIONS, ACTIVITY_OPTIONS, GROUP_PURPOSE_OPTIONS, TX_TYPE_OPTIONS, CURRENCY_OPTIONS } from "../../../constants";

export const CompanyProfileForm = ({ data, onSubmit, countries = [], loading = false }) => {
  const [formData, setFormData] = useState(data);
  const [countryQuery, setCountryQuery] = useState("");
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showActivityOther, setShowActivityOther] = useState(false);

  // Check if current activity is "Other" or custom on mount
  useEffect(() => {
    if (formData.business_activity) {
      const isKnownActivity = ACTIVITY_OPTIONS.includes(formData.business_activity);
      setShowActivityOther(!isKnownActivity || formData.business_activity === "Other");
    }
  }, [formData.business_activity]);

  // Determine initial FS mode
  const [fsMode, setFsMode] = useState(() => {
    if (data.fs_last_bookyear) return "Y";
    if (data.shelf === "Y") return "NA";
    return "N";
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    // If "Other" with custom value, use the custom value
    const submitData = { ...formData };
    if (showActivityOther && formData.business_activity_other?.trim()) {
      submitData.business_activity = formData.business_activity_other.trim();
    }

    await onSubmit({
      ...submitData,
      transaction_profile: submitData.transaction_profile || null,
      group_member: submitData.group_member || null,
      foreign_bank_account: submitData.foreign_bank_account || null,
      pep: submitData.pep || null,
      listed: submitData.listed || null,
      bank_account: submitData.bank_account || null,
      shelf: submitData.shelf || null,
      ubo_info_up_to_date: submitData.ubo_info_up_to_date || null,
      ubo_origins: submitData.ubo_origins?.length ? submitData.ubo_origins : null,
      total_assets_currency: submitData.total_assets_currency || null,
      relationship_started_at: submitData.relationship_started_at || null,
      total_assets: submitData.total_assets === "" ? null : Number(String(submitData.total_assets).replace(/[^\d.]/g, "")),
    });
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleActivityChange = (value) => {
    handleChange("business_activity", value);
    setShowActivityOther(value === "Other");
    if (value !== "Other") {
      handleChange("business_activity_other", "");
    }
  };

  const handleTransactionTypeToggle = (typeValue) => {
    const current = new Set(formData.transaction_types || []);
    if (current.has(typeValue)) {
      current.delete(typeValue);
    } else {
      current.add(typeValue);
    }
    handleChange("transaction_types", Array.from(current));
  };

  const filteredCountries = useMemo(() => countries.filter((c) => c.name.toLowerCase().includes(countryQuery.toLowerCase())).slice(0, 10), [countries, countryQuery]);

  const handleUboOriginAdd = (countryName) => {
    if (!formData.ubo_origins.includes(countryName)) {
      handleChange("ubo_origins", [...formData.ubo_origins, countryName]);
    }
    setCountryQuery("");
    setShowCountryDropdown(false);
  };

  const handleUboOriginRemove = (countryName) => {
    handleChange(
      "ubo_origins",
      formData.ubo_origins.filter((x) => x !== countryName)
    );
  };

  const handleFsModeChange = (mode) => {
    setFsMode(mode);
    if (mode !== "Y") {
      handleChange("fs_last_bookyear", "");
    }
    if (mode === "NA") {
      handleChange("shelf", "Y");
    } else if (mode === "N") {
      handleChange("shelf", "N");
    }
  };

  return (
    <form className="card form-section" onSubmit={handleSubmit}>
      <h3>Profile (used in Export)</h3>
      <div className="form-grid">
        <Field label="Services">
          <select value={formData.services || ""} onChange={(e) => handleChange("services", e.target.value)}>
            <option value="">—</option>
            {SERVICE_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Business activity">
          <select value={showActivityOther ? "Other" : formData.business_activity || ""} onChange={(e) => handleActivityChange(e.target.value)}>
            <option value="">—</option>
            {ACTIVITY_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
          {showActivityOther && (
            <input
              type="text"
              placeholder="Specify business activity..."
              value={formData.business_activity_other || formData.business_activity || ""}
              onChange={(e) => handleChange("business_activity_other", e.target.value)}
              style={{ marginTop: "8px" }}
            />
          )}
        </Field>

        <Field label="Group member">
          <ToggleYN value={formData.group_member} onChange={(v) => handleChange("group_member", v)} />
        </Field>

        <Field label="Group purpose">
          <select value={formData.group_purpose || ""} onChange={(e) => handleChange("group_purpose", e.target.value)}>
            <option value="">—</option>
            {GROUP_PURPOSE_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Relationship started at">
          <input type="date" value={formData.relationship_started_at || ""} onChange={(e) => handleChange("relationship_started_at", e.target.value)} />
        </Field>

        <Field label="Total assets">
          <input type="number" value={formData.total_assets || ""} onChange={(e) => handleChange("total_assets", e.target.value)} placeholder="e.g., 250000" />
        </Field>

        <Field label="Assets currency">
          <select value={formData.total_assets_currency || ""} onChange={(e) => handleChange("total_assets_currency", e.target.value)}>
            {CURRENCY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Transaction profile">
          <ToggleYN value={formData.transaction_profile} onChange={(v) => handleChange("transaction_profile", v)} />
        </Field>

        <Field label="Transaction types" style={{ gridColumn: "1 / -1" }}>
          <div className="chip-group">
            {TX_TYPE_OPTIONS.map((o) => {
              const checked = (formData.transaction_types || []).includes(o.value);
              return (
                <label key={o.value} className={`chip-toggle ${checked ? "is-checked" : ""}`}>
                  <input type="checkbox" checked={checked} onChange={() => handleTransactionTypeToggle(o.value)} />
                  <span>{o.label}</span>
                </label>
              );
            })}
          </div>
          <div className="chip-actions">
            <button
              type="button"
              className="text-btn button-plain"
              onClick={() =>
                handleChange(
                  "transaction_types",
                  TX_TYPE_OPTIONS.map((x) => x.value)
                )
              }>
              Select all
            </button>
            <button type="button" className="text-btn button-plain" onClick={() => handleChange("transaction_types", [])}>
              Clear
            </button>
          </div>
        </Field>

        <Field label="Foreign bank account">
          <ToggleYN value={formData.foreign_bank_account} onChange={(v) => handleChange("foreign_bank_account", v)} />
        </Field>

        <Field label="UBO origins" style={{ gridColumn: "1 / -1" }}>
          <div className="chip-select">
            <input
              type="text"
              placeholder="Search and select countries..."
              value={countryQuery}
              onChange={(e) => {
                setCountryQuery(e.target.value);
                setShowCountryDropdown(true);
              }}
              onFocus={() => setShowCountryDropdown(true)}
              className="chip-input"
            />

            {showCountryDropdown && countryQuery && filteredCountries.length > 0 && (
              <ul className="chip-dropdown">
                {filteredCountries.map((c) => (
                  <li key={c.id} onClick={() => handleUboOriginAdd(c.name)} onMouseDown={(e) => e.preventDefault()}>
                    {c.name}
                  </li>
                ))}
              </ul>
            )}

            {(formData.ubo_origins || []).length > 0 && (
              <div className="chip-list">
                {formData.ubo_origins.map((name) => (
                  <span key={name} className="chip">
                    {name}
                    <button type="button" className="chip-remove" onClick={() => handleUboOriginRemove(name)} aria-label={`Remove ${name}`}>
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </Field>

        <Field label="UBO info up-to-date?">
          <ToggleYN value={formData.ubo_info_up_to_date} onChange={(v) => handleChange("ubo_info_up_to_date", v)} />
        </Field>

        <Field label="Financial statements">
          <div className="radio-group">
            <label className="radio-pill">
              <input type="radio" name="fs_mode" value="Y" checked={fsMode === "Y"} onChange={() => handleFsModeChange("Y")} />
              <span>Yes</span>
            </label>
            <label className="radio-pill">
              <input type="radio" name="fs_mode" value="N" checked={fsMode === "N"} onChange={() => handleFsModeChange("N")} />
              <span>No</span>
            </label>
            <label className="radio-pill">
              <input type="radio" name="fs_mode" value="NA" checked={fsMode === "NA"} onChange={() => handleFsModeChange("NA")} />
              <span>N/A</span>
            </label>
          </div>
          {fsMode === "Y" && (
            <input
              type="number"
              min="1900"
              max="2100"
              value={formData.fs_last_bookyear || ""}
              onChange={(e) => handleChange("fs_last_bookyear", e.target.value)}
              placeholder="Year (e.g., 2025)"
              style={{ marginTop: "8px" }}
            />
          )}
        </Field>

        <Field label="PEP">
          <ToggleYN value={formData.pep} onChange={(v) => handleChange("pep", v)} />
        </Field>

        <Field label="Listed">
          <ToggleYN value={formData.listed} onChange={(v) => handleChange("listed", v)} />
        </Field>

        <Field label="Bank account">
          <ToggleYN value={formData.bank_account} onChange={(v) => handleChange("bank_account", v)} />
        </Field>

        <Field label="Shelf">
          <ToggleYN value={formData.shelf} onChange={(v) => handleChange("shelf", v)} />
        </Field>
      </div>
      <div className="form-actions">
        <button type="submit" disabled={loading}>
          {loading ? "Saving..." : "Save Profile"}
        </button>
      </div>
    </form>
  );
};
